/**
 * Pure builders for the onboarding persistence mutations.
 *
 * These were extracted from `useOnboardingSubmit.handleComplete` so the final
 * submit and the per-field autosave (`useOnboardingFieldPersistence`) construct
 * identical GraphQL inputs from the same source and cannot drift apart.
 */
import { hasMeaningfulOnboardingValue } from '@/lib/ai/onboardingFieldValues'
import { mergeCompanyMetaUpdateInput } from '@/lib/companyMetaUpdatePayload'

import { normalizeCommissionForApi } from './onboardingCommission'
import { legalLinkForSubmit, normalizeHttpsUrl } from './onboardingLegalUrls'
import type { OnboardingFormData } from './onboardingPrefill'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type CompanyData = Record<string, any>

export type CompanyCoreInputOptions = {
  isUsOnboarding: boolean
  countryValueForSubmit: () => string | null
}

export function buildCompanyCoreInput(
  formData: OnboardingFormData,
  company: CompanyData,
  { isUsOnboarding, countryValueForSubmit }: CompanyCoreInputOptions
) {
  const websiteNorm = normalizeHttpsUrl(formData.website)
  const imprint = legalLinkForSubmit(formData.linkImprint, websiteNorm)
  const privacy = legalLinkForSubmit(formData.linkPrivacy, websiteNorm)
  const about = legalLinkForSubmit(formData.linkAbout, websiteNorm)
  return {
    companyId: company.id,
    name: formData.name,
    links: {
      website: websiteNorm || formData.website || null,
      imprint: imprint || company.links?.imprint || null,
      privacy: privacy || company.links?.privacy || null,
      about: about || company.links?.about || null,
    },
    address: {
      street: formData.street?.trim() || null,
      zip: formData.zipcode?.trim() || null,
      city: formData.city?.trim() || null,
      country: countryValueForSubmit(),
      ...(isUsOnboarding ? { state: formData.state?.trim() || null } : {}),
    },
    commission: normalizeCommissionForApi(formData.commission) || null,
    ...(isUsOnboarding
      ? {
          brokerageName: formData.brokerageName?.trim() || null,
          licenseNumber: formData.licenseNumber?.trim() || null,
        }
      : {}),
  }
}

/** Theme colors payload, or `null` when neither color has a meaningful value. */
export function buildThemeColors(
  formData: OnboardingFormData,
  company?: CompanyData
): { primary: string; secondary: string } | null {
  const primary = hasMeaningfulOnboardingValue('primaryColor', formData.primaryColor)
    ? formData.primaryColor.trim()
    : null
  const secondary = hasMeaningfulOnboardingValue('secondaryColor', formData.secondaryColor)
    ? formData.secondaryColor.trim()
    : null
  if (!primary && !secondary) return null
  // The backend's ColorInput requires `primary` and `secondary` together, so a
  // partial edit (only one color set) can't be sent as-is. Backfill the missing
  // color from the company's existing theme — falling back to black — matching
  // the AI colour-confirmation flow in useAiActionHandler.
  const existing = (company?.theme?.colors ?? {}) as { primary?: string; secondary?: string }
  return {
    primary: primary ?? existing.primary ?? '#000000',
    secondary: secondary ?? existing.secondary ?? '#000000',
  }
}

/**
 * Slogan value for the update mutation:
 *  - the trimmed slogan when the user entered one,
 *  - `''` to explicitly clear a slogan the user removed (the mutation input
 *    accepts an empty string), so autosave/submit actually persist the deletion,
 *  - `null` when there is nothing to persist (empty field, none stored) so the
 *    mutation is skipped.
 */
export function buildSloganValue(
  formData: OnboardingFormData,
  company?: CompanyData
): string | null {
  const slogan = formData.slogan?.trim()
  if (slogan) return slogan
  const existing = typeof company?.slogan === 'string' ? company.slogan.trim() : ''
  return existing ? '' : null
}

/** Company-meta update input, or `null` when there is nothing to persist. */
export function buildMetaInput(formData: OnboardingFormData, company: CompanyData) {
  const description = formData.businessDescription?.trim()
  if (!description) return null
  return mergeCompanyMetaUpdateInput(company, { businessDescription: description })
}

export function buildAmbassadorInput(formData: OnboardingFormData, company: CompanyData) {
  return {
    companyId: company.id,
    salutation: (formData.ambassadorSalutation || '').trim(),
    firstname: (formData.ambassadorFirstName || '').trim(),
    lastname: (formData.ambassadorLastName || '').trim(),
    role: (formData.ambassadorRole || '').trim(),
    email: (formData.ambassadorEmail || '').trim(),
    phone: (formData.phone || '').trim(),
    mobile: (formData.ambassadorMobile || '').trim(),
  }
}

/**
 * GENERAL contact-person upsert input. `existingId` is the id of the current
 * GENERAL contact (if any) — omit it from the mutation variables to create one.
 */
export function buildGeneralContactInput(formData: OnboardingFormData, company: CompanyData) {
  const general = (company.contactPersons || []).find(
    (person: { type?: string }) => person.type === 'GENERAL'
  )
  const input = {
    companyId: company.id,
    type: 'GENERAL',
    companyName: general?.companyName ?? null,
    firstname: (formData.contactFirstName || '').trim(),
    lastname: (formData.contactLastName || '').trim(),
    email: (formData.contactEmail || '').trim(),
    phone: (formData.contactPhone || '').trim(),
  }
  return { input, existingId: general?.id as string | undefined }
}
